import {
  AgxSelectChip,
  AgxSpacer,
  AgxTextInput,
  Icon_MagnifyingGlass,
  SelectChipOption,
} from '@urbanx/agx.ui-components2';

import './ContractsDashboard.scss';
import { useDebouncedState } from '@urbanx/agx-ui-components';
import { useEffect } from 'react';

interface CampaignsDashboardHeaderProps {
  addressSearchText?: string;
  agencyFilterOptions: SelectChipOption[];
  leadAgentFilterOptions: SelectChipOption[];
  hasAgencySelection: boolean;
  isLeadAgentFilterPending: boolean;
  selectedStateOptions: SelectChipOption[];
  selectedContractStatusOptions: SelectChipOption[];
  selectedSettlementDateOptions: SelectChipOption[];
  selectedVendorBankDetailsOptions: SelectChipOption[];
  onAddressSearchTextChange: (value?: string) => void;
  handleStateFilterSelect: (options: SelectChipOption[]) => void;
  handleAgencyFilterSelect: (options: SelectChipOption[]) => void;
  handleLeadAgentFilterSelect: (options: SelectChipOption[]) => void;
  handleContractStatusFilterSelect: (options: SelectChipOption[]) => void;
  handleSettlementDateFilterSelect: (options: SelectChipOption[]) => void;
  handleVendorBankDetailsFilterSelect: (options: SelectChipOption[]) => void;
  resetAgencyFilters: () => void;
  resetLeadAgentFilters: () => void;
  resetStateFilters: () => void;
  resetContractStatusFilters: () => void;
  resetSettlementDateFilters: () => void;
  resetVendorBankDetailsFilters: () => void;
}

const CampaignsDashboardHeader = ({
  addressSearchText,
  agencyFilterOptions,
  leadAgentFilterOptions,
  hasAgencySelection,
  isLeadAgentFilterPending,
  selectedStateOptions,
  selectedContractStatusOptions,
  selectedSettlementDateOptions,
  selectedVendorBankDetailsOptions,
  onAddressSearchTextChange,
  handleStateFilterSelect,
  handleAgencyFilterSelect,
  handleLeadAgentFilterSelect,
  handleContractStatusFilterSelect,
  handleSettlementDateFilterSelect,
  handleVendorBankDetailsFilterSelect,
  resetAgencyFilters,
  resetLeadAgentFilters,
  resetStateFilters,
  resetContractStatusFilters,
  resetSettlementDateFilters,
  resetVendorBankDetailsFilters,
}: CampaignsDashboardHeaderProps) => {
  const [searchText, setSearchText] = useDebouncedState(addressSearchText, 500);

  useEffect(() => {
    onAddressSearchTextChange(searchText);
  }, [onAddressSearchTextChange, searchText]);

  return (
    <AgxSpacer
      fill
      contentAlignment="center"
      direction="horizontal"
      className="contractDashboardFilters"
    >
      <AgxSpacer className="contractDashboardSearch">
        <AgxTextInput
          id={'address-search'}
          type={'text'}
          placeholder={'Search by address'}
          defaultValue={addressSearchText}
          border="rounded"
          hideOptionalText
          leftIcon={
            <Icon_MagnifyingGlass color="var(--fg-tertiary, #B9B9B9)" height={15} width={15} />
          }
          onChange={(v) => setSearchText(v)}
          minWidth="320px"
          noHeader
        />
      </AgxSpacer>
      <AgxSpacer
        direction="horizontal"
        gap="xs"
        contentJustification="end"
        className="contractDashboardFilterControls"
        wrap
      >
        <AgxSelectChip
          id="contract-dashboard-state-filter"
          options={selectedStateOptions}
          hideSelectAll
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="State"
          onSelect={handleStateFilterSelect}
          onReset={resetStateFilters}
        />
        <AgxSelectChip
          id="contract-dashboard-agency-filter"
          options={agencyFilterOptions}
          hideSelectAll
          searchPlaceHolder="Search agencies"
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="Agency"
          onSelect={handleAgencyFilterSelect}
          onReset={resetAgencyFilters}
        />
        <AgxSelectChip
          id="contract-dashboard-lead-agent-filter"
          options={leadAgentFilterOptions}
          hideSelectAll
          searchPlaceHolder="Search lead agents"
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="Lead Agent"
          emptyMessage={
            hasAgencySelection ? 'No lead agents found' : 'Select an agency to filter lead agents'
          }
          disabled={hasAgencySelection && isLeadAgentFilterPending}
          onSelect={handleLeadAgentFilterSelect}
          onReset={resetLeadAgentFilters}
        />
        <AgxSelectChip
          id="contract-dashboard-contract-status-filter"
          options={selectedContractStatusOptions}
          hideSelectAll
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="Contract Status"
          onSelect={handleContractStatusFilterSelect}
          onReset={resetContractStatusFilters}
        />
        <AgxSelectChip
          id="contract-dashboard-settlementDate-filter"
          options={selectedSettlementDateOptions}
          variant="chip"
          type="single-select"
          size="medium"
          align="right"
          label="Settlement Date"
          onSelect={handleSettlementDateFilterSelect}
          onReset={resetSettlementDateFilters}
        />
        <AgxSelectChip
          id="contract-dashboard-bankDetails-filter"
          options={selectedVendorBankDetailsOptions}
          variant="chip"
          hideSelectAll
          type="multi-select"
          size="medium"
          align="right"
          label="Vendor Bank Details"
          onSelect={handleVendorBankDetailsFilterSelect}
          onReset={resetVendorBankDetailsFilters}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default CampaignsDashboardHeader;
